import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { clamp } from 'lodash-es';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useRef } from 'react';
import styles from '../GanttChart.module.scss';
import type { GanttCellParams } from '../gantt-types';
import type { GanttZoomLevel } from '../gantt-zoom';

interface Props {
  zoom: GanttZoomLevel;
  dayColumns: Array<DateTime<true>>;
  scrollLeft: number;
  width: number;
}

interface Column {
  date: DateTime<true>;
  width: number;
  offset: number;
}

const CURRENT_YEAR = DateTime.now().year;
const GanttChartMonthGrid: React.FC<Props> = ({ zoom, dayColumns, scrollLeft }) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const monthColumns: Array<Column> = useMemo(() => {
    const columns: Array<Column> = [];
    let totalWidth = 0;
    for (let index = 0; index < dayColumns.length; index++) {
      const date = dayColumns[index];
      const span = date.daysInMonth - (date.day - 1);
      const spanOffset = span - 1;
      const width = span * zoom.columnWidth;

      columns.push({
        date,
        width,
        offset: totalWidth,
      });
      totalWidth += width;
      index += spanOffset;
    }
    return columns;
  }, [dayColumns, zoom]);

  const getMonthColumnWidth = useCallback(
    (params: { index: number }) => monthColumns[params.index].width,
    [monthColumns]
  );
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: monthColumns.length,
    getScrollElement: () => gridRef?.current,
    estimateSize: index => getMonthColumnWidth({ index }),
  });

  React.useLayoutEffect(() => {
    columnVirtualizer.scrollToOffset(scrollLeft);
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  }, [scrollLeft]);

  // Keep track of month label widths so that we can make them sticky during horizontal scroll.
  const monthLabelWidths = useRef<Record<number, number>>({});
  const setMonthLabelWidth = useCallback(
    (columnIndex: number, element: HTMLSpanElement | null) => {
      monthLabelWidths.current[columnIndex] = element?.offsetWidth ?? 0;
    },
    // We show years on labels in some zoom levels, not others
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
    [zoom.view]
  );

  const renderCell = useCallback(
    ({ columnIndex, key }: GanttCellParams) => {
      const column = monthColumns[columnIndex];
      let label = column.date.toFormat('MMMM');
      if (column.date.year !== CURRENT_YEAR && zoom.view !== 'month') {
        label = `${label} ${column.date.year}`;
      }
      const labelWidth = monthLabelWidths.current[columnIndex] ?? 0;
      const PADDING = 16;
      const left = clamp(scrollLeft - column.offset, 0, column.width - labelWidth - PADDING * 2);
      return (
        <div
          className={classNames(styles.topHeaderMonthCell, {
            [styles.firstCell]: columnIndex === 0,
          })}
          key={key}
          style={{ paddingLeft: PADDING }}
        >
          <div className={styles.label} style={{ left }}>
            <span ref={el => setMonthLabelWidth(columnIndex, el)}>{label}</span>
          </div>
        </div>
      );
    },
    [monthColumns, scrollLeft, monthLabelWidths, setMonthLabelWidth, zoom.view]
  );

  return (
    <div className={styles.topHeaderMonthRow} ref={gridRef}>
      <div
        style={{
          width: columnVirtualizer.getTotalSize(),
          height: '100%',
          position: 'relative',
        }}
      >
        {columnVirtualizer.getVirtualItems().map(virtualColumn => (
          <div
            key={virtualColumn.key}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: `translateX(${virtualColumn.start}px)`,
            }}
          >
            <div
              style={{
                width: getMonthColumnWidth({ index: virtualColumn.index }),
                height: 24,
                overflow: 'hidden',
              }}
            >
              {renderCell({
                columnIndex: virtualColumn.index,
                key: virtualColumn.key,
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default GanttChartMonthGrid;
